import { FC, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  getJobPrefrences,
  getWeekDays,
  updateAvailabilityPreference,
} from "../../../../core/services/adi-service";
import Skeleton from "@mui/material/Skeleton";
import Button from "../../shared/elements/Button";
import Notification from "../../shared/overlays/Notification";
import toast from "react-hot-toast";
import {
  ExclamationCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { DayModel } from "../../../../core/models/day-model";
import { JobPreferenceDataModel } from "../../../../core/models/job-preference-data-model";
import { Availability } from "./Availability";
import PageHeader from "../../shared/elements/PageHeader";
import { useHistory } from "react-router-dom";

const AvailabilityPreference: FC = () => {
  const [weekDays, setWeekDays] = useState<DayModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [overLap, setOverLap] = useState<boolean[]>([]);
  const [fullJobPreference, setFullJobPreference] = useState<JobPreferenceDataModel | null>(null);
  const history = useHistory();
  const redirectToSettings = () => {
    history.push("/ADI/settings/overview");
  };

  const validationSchema = Yup.object().shape({
    availability: Yup.array()
      .min(1, "Availability is required")
      .of(
        Yup.object().shape({
          day_id: Yup.number().required(),
          from_time: Yup.string().required(),
          to_time: Yup.string().required(),
        })
      ),
  });

  const formik = useFormik({
    initialValues: { availability: [] },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values, { setSubmitting, setStatus }) => {
      setStatus("");
      const hasError = checkAvailabilityError(values.availability);
      const hasOverlap = checkOverlap(values.availability);

      if (hasError || hasOverlap || !fullJobPreference) {
        setSubmitting(false);
        return;
      }

      const payload: JobPreferenceDataModel = {
        ...fullJobPreference,
        availability: values.availability,
      };

      const toastId = toast.custom(() => (
        <Notification
          colour="gray"
          title="Saving availability..."
          description=""
          Icon={ExclamationCircleIcon}
        />
      ));

      updateAvailabilityPreference(payload)
        .then(() => {
          toast.custom(() => (
            <Notification
              colour="green"
              title="Saved successfully"
              description=""
              Icon={CheckCircleIcon}
            />
          ), { id: toastId });

          redirectToSettings();
        })
        .catch(() => {
          toast.custom(() => (
            <Notification
              colour="red"
              title="Failed to save availability"
              description=""
              Icon={ExclamationTriangleIcon}
            />
          ), { id: toastId });

          setStatus("Error saving availability.");
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });

  const checkAvailabilityError = (slots: any[]) => {
    return slots.length === 0 || slots.some(s => !s.day_id || !s.from_time || !s.to_time || s.from_time >= s.to_time);
  };

  const checkOverlap = (slots: any[]) => {
    const overlaps: boolean[] = [];
    weekDays.forEach((day) => {
      const daySlots = slots.filter((s) => s.day_id === day.id);
      overlaps.push(isOverlapping(daySlots));
    });
    setOverLap(overlaps);
    return overlaps.includes(true);
  };

  const isOverlapping = (arr: any[]) => {
    const toMinutes = (time: string) => {
      const [h, m] = time.split(":").map(Number);
      return h * 60 + m;
    };
    for (let i = 0; i < arr.length - 1; i++) {
      for (let j = i + 1; j < arr.length; j++) {
        if (
          toMinutes(arr[i].to_time) > toMinutes(arr[j].from_time) &&
          toMinutes(arr[j].to_time) > toMinutes(arr[i].from_time)
        ) {
          return true;
        }
      }
    }
    return false;
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([getWeekDays(), getJobPrefrences()]).then(([weekRes, prefRes]) => {
      const weekdays = weekRes.data.results;
      const prefData = prefRes.data.results;

      const mappedAvailability = prefData.availability.map((item: any) => ({
        ...item,
        from_time: item.from_time.slice(0, 5),
        to_time: item.to_time.slice(0, 5),
      }));

      setWeekDays(weekdays);
      setFullJobPreference(prefData);
      formik.setFieldValue("availability", mappedAvailability);
      setLoading(false);
    });
  }, []);

  return (
    <div>
      <div className='mb-5'>
            <PageHeader title='Availability' backToURL='/ADI/settings/overview' />
      </div>
      <div className="bg-white p-6 rounded shadow">
        <form onSubmit={formik.handleSubmit}>
          {formik.status && <p className="text-sm text-red-600">{formik.status}</p>}

          {loading ? (
            <Skeleton height={100} />
          ) : (
            <Availability
              loading={loading}
              formik={formik}
              weekDays={weekDays}
              overLap={overLap}
              availabilityList={formik.values.availability}
            />
          )}

          <div className="my-5 grid grid-cols-2 gap-2">
            {loading ? <Skeleton width={150} height={50} animation="wave"></Skeleton> :
              <Button
                type='submit'
                title='Save'
                disabled={formik.isSubmitting}
                colour="yellow"
              >
                <span className='indicator-label'> Save</span>
              </Button>}

            {loading ? <Skeleton width={150} height={50} animation="wave"></Skeleton> :
              <Button
                disabled={formik.isSubmitting}
                onClick={() => redirectToSettings()}
                colour="outline"
                size="large"
              >
                <span className='indicator-label'> Cancel</span>
              </Button>}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AvailabilityPreference;
